import { createActionHandler, createReducer } from '@pressreader/appstore';
import { IAuthError, IUser, IUserSettings, LibraryPostAuthOptions } from '@pressreader/authentication-types';

import {
    authInitialized,
    clearLibraryPostAuthOptionsAction,
    loginDone,
    loginFailed,
    logOutDone,
    logOutFailed,
    maxNumberOfEmailsReachedEvent,
    pwdRecoveryBegan,
    pwdRecoveryDone,
    pwdRecoveryFailed,
    registrationEmailVerifiedEvent,
    resetAuthStateAction,
    resetEmailVerifiedStatus,
    signUpDone,
    signUpFailed,
    userAvatarChanged,
    userChangeBegan,
    userChangeDone,
    userChangeFailed,
} from './actions';
import { createAnonymousUser, createDefaultUserSettings } from './utils';

interface State {
    initialized: boolean;

    authInProgress: boolean;
    lastAuthError: IAuthError | null;

    lastRecoveryEmailSentTo: string | null;

    user: IUser;
    userSettings: IUserSettings;
    maxNumberOfEmailsReached: boolean;
    emailVerified: boolean;
    libraryPostAuthOptions?: LibraryPostAuthOptions;
}

const initialState: State = {
    initialized: false,

    authInProgress: false,
    lastAuthError: null,
    lastRecoveryEmailSentTo: null,

    user: createAnonymousUser(),
    userSettings: createDefaultUserSettings(),
    maxNumberOfEmailsReached: false,
    emailVerified: false,
    libraryPostAuthOptions: undefined,
};

const reducer = createReducer<State>(
    initialState,
    createActionHandler(
        authInitialized,
        (state, action): State => ({
            ...state,
            initialized: true,
            user: action.payload.user,
            userSettings: action.payload.userSettings,
        }),
    ),
    createActionHandler(
        loginDone,
        (state, action): State => ({
            ...state,
            user: action.payload.user,
            userSettings: action.payload.userSettings,
            libraryPostAuthOptions: { ...action.payload.authOptions },
        }),
    ),
    createActionHandler(
        signUpDone,
        (state, action): State => ({
            ...state,
            user: action.payload.user,
            userSettings: action.payload.userSettings,
            libraryPostAuthOptions: undefined,
        }),
    ),
    createActionHandler(
        logOutDone,
        (state): State => ({
            ...state,
            user: createAnonymousUser(),
            userSettings: createDefaultUserSettings(),
            libraryPostAuthOptions: undefined,
        }),
    ),
    createActionHandler(
        userChangeBegan,
        (state): State => ({
            ...state,
            authInProgress: true,
            lastAuthError: null,
        }),
    ),
    createActionHandler(
        userChangeDone,
        (state): State => ({
            ...state,
            authInProgress: false,
            lastAuthError: null,
        }),
    ),
    createActionHandler(
        userChangeFailed,
        (state): State => ({
            ...state,
            authInProgress: false,
        }),
    ),
    createActionHandler(
        loginFailed,
        (state, action): State => ({
            ...state,
            lastAuthError: action.payload.error,
        }),
    ),
    createActionHandler(
        logOutFailed,
        (state, action): State => ({
            ...state,
            lastAuthError: action.payload.error,
        }),
    ),
    createActionHandler(
        signUpFailed,
        (state, action): State => ({
            ...state,
            lastAuthError: action.payload.error,
        }),
    ),
    createActionHandler(
        pwdRecoveryBegan,
        (state): State => ({
            ...state,
            lastAuthError: null,
        }),
    ),
    createActionHandler(
        pwdRecoveryDone,
        (state, action): State => ({
            ...state,
            lastAuthError: null,
            lastRecoveryEmailSentTo: action.payload.email,
        }),
    ),
    createActionHandler(
        pwdRecoveryFailed,
        (state, action): State => ({
            ...state,
            lastAuthError: { message: action.payload.error },
        }),
    ),
    createActionHandler(
        resetAuthStateAction,
        (state): State => ({
            ...state,
            lastAuthError: null,
            lastRecoveryEmailSentTo: null,
        }),
    ),
    createActionHandler(
        userAvatarChanged,
        (state, action): State => ({
            ...state,
            user: { ...state.user, avatarImageId: action.payload.imageId },
        }),
    ),
    createActionHandler(
        maxNumberOfEmailsReachedEvent,
        (state): State => ({
            ...state,
            maxNumberOfEmailsReached: true,
        }),
    ),
    createActionHandler(
        registrationEmailVerifiedEvent,
        (state): State => ({
            ...state,
            emailVerified: true,
        }),
    ),
    createActionHandler(
        resetEmailVerifiedStatus,
        (state): State => ({
            ...state,
            emailVerified: false,
        }),
    ),
    createActionHandler(
        clearLibraryPostAuthOptionsAction,
        (state): State => ({
            ...state,
            libraryPostAuthOptions: undefined,
        }),
    ),
);

export { initialState, reducer, State };
